"use client";

import type React from "react";
import { createContext, useState, useContext, useEffect } from "react";

export type FontSizeScale =
  | "small"
  | "normal"
  | "large"
  | "extra-large"
  | "xx-large";

type FontSizeContextType = {
  fontSizeScale: FontSizeScale;
  setFontSizeScale: (scale: FontSizeScale) => void;
  isAccessibleSize: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const FontSizeContext = createContext<FontSizeContextType | undefined>(
  undefined
);

const STORAGE_KEY = "selfserve-font-size-scale";

// Font scale factors for Tailwind CSS
export const FONT_SCALE_FACTORS = {
  small: 0.875, // 87.5% - for users who prefer smaller text
  normal: 1.0, // 100% - default size
  large: 1.125, // 112.5% - for users who need larger text
  "extra-large": 1.25, // 125% - for users with significant vision needs
  "xx-large": 1.5, // 150% - for elderly users who need very large text
} as const;

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [fontSizeScale, setFontSizeScaleState] =
    useState<FontSizeScale>("normal");
  const [isInitialized, setIsInitialized] = useState(false);

  // Load font size preference from localStorage on mount
  useEffect(() => {
    try {
      const savedScale = localStorage.getItem(STORAGE_KEY) as FontSizeScale;
      if (
        savedScale &&
        ["small", "normal", "large", "extra-large", "xx-large"].includes(
          savedScale
        )
      ) {
        setFontSizeScaleState(savedScale);
      }
    } catch (error) {
      // Handle localStorage errors gracefully (e.g., private browsing mode)
      console.warn(
        "Failed to load font size preference from localStorage:",
        error
      );
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // Save font size preference to localStorage and update CSS
  const setFontSizeScale = (scale: FontSizeScale) => {
    setFontSizeScaleState(scale);

    try {
      localStorage.setItem(STORAGE_KEY, scale);
    } catch (error) {
      // Handle localStorage errors gracefully (e.g., quota exceeded)
      console.warn(
        "Failed to save font size preference to localStorage:",
        error
      );
    }

    // Update CSS custom property for global scaling
    const factor = FONT_SCALE_FACTORS[scale];
    document.documentElement.style.setProperty(
      "--font-scale-factor",
      factor.toString()
    );

    // Dispatch a custom event to notify components
    window.dispatchEvent(new CustomEvent("fontSizeChanged", { detail: scale }));
  };

  // Initialize CSS custom property on mount and when scale changes
  useEffect(() => {
    if (isInitialized) {
      const factor = FONT_SCALE_FACTORS[fontSizeScale];
      document.documentElement.style.setProperty(
        "--font-scale-factor",
        factor.toString()
      );
    }
  }, [fontSizeScale, isInitialized]);

  // Check if current scale meets accessibility guidelines
  const isAccessibleSize =
    fontSizeScale === "normal" ||
    fontSizeScale === "large" ||
    fontSizeScale === "extra-large" ||
    fontSizeScale === "xx-large";

  return (
    <FontSizeContext.Provider
      value={{ fontSizeScale, setFontSizeScale, isAccessibleSize }}
    >
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error("useFontSize must be used within a FontSizeProvider");
  }
  return context;
};




